import React, { useState } from 'react';
import HealthDataVerticalTable from '../../../HealthData/Components/Tables/HealthDataVerticalTable';
import { Table } from '../../../HealthData/Enums/Table.enum';
import { IHealthData } from '../../../HealthData/Hooks/useHealthData';
import OverlayLoading from '../../../../Components/OverlayLoading';
import useTranslation from '../../../../Hooks/useTranslation';
import useQuestionnaireResultTable from '../../Hooks/useQuestionnaireResultTable';
import { useEffectAsync } from '../../../../helpers';
import { flatten } from 'lodash';
import { QuestionnairesResultsReStructureScheme } from '../../interfaces';
import { getDate } from '../../../../Helpers/date.helper';
import useQuestionnaireResults from '../../Hooks/useQuestionnaireResults';
import { Moment } from 'moment';

export interface MonitoringDataset {
	results: IHealthData[];
}

interface Props {
	patientId: string;
	questionnairesCodes: string[];
	tableName?: string;
	structure?: QuestionnairesResultsReStructureScheme[];
	roundValuesTo?: number;
	from?: Moment;
	to?: Moment;
	filterKeys?: string[];
}

const QuestionnairesResults: React.FC<Props> = ({
	patientId,
	questionnairesCodes,
	tableName,
	structure,
	roundValuesTo,
	from,
	to,
	filterKeys,
}) => {
	const [dataset, setDataset] = useState<any>({ results: [] });
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();
	const { getDataAsHealthData } = useQuestionnaireResultTable();
	const { getQuestionnaireNameByQuestionnaireCode } = useQuestionnaireResults();

	useEffectAsync(async () => {
		setIsLoading(true);
		const healthData: any[] = [];
		await Promise.all(
			questionnairesCodes.map(async code => {
				const questionnairesResultsAsHealthData = flatten(
					Object.values(
						await getDataAsHealthData({
							patientId,
							questionnaireCode: code,
							questionnaireName: getQuestionnaireNameByQuestionnaireCode(code),
							t,
							from,
							to,
						}),
					),
				);
				questionnairesResultsAsHealthData.map(item => {
					const exist = healthData.findIndex(({ key }) => key === item.key);
					if (exist > 0) {
						healthData[exist].data = [...healthData[exist].data, ...item.data];
					} else {
						healthData.push(item);
					}
				});
			}),
		);

		if (structure) {
			restructure(structure, healthData, filterKeys);
		} else {
			setDataset({
				results: healthData,
			} as MonitoringDataset);
		}

		setIsLoading(false);
	}, [from, to, questionnairesCodes]);

	const restructure = (
		structure: QuestionnairesResultsReStructureScheme[],
		sourceData: any[],
		filterKeys?: string[],
	) => {
		const healthData: any[] = [];
		structure.map(
			({
				title,
				contains,
				key,
				unit,
				renderEmpty,
				filterSensitive,
				emptyRules,
			}) => {
				if (filterSensitive && filterKeys?.length) {
					if (!filterKeys.includes(key)) {
						return;
					}
				}
				const data: any = [];
				contains.map(pairKey => {
					const finded = sourceData.find((d: IHealthData) => d.key === pairKey);
					if (finded?.data) {
						for (const a of finded.data) {
							data.push(a);
						}
					}
				});

				if (emptyRules) {
					const ruleData = sourceData.find(
						(data: IHealthData) => data.key === emptyRules.key,
					);
					if (ruleData) {
						for (const a of ruleData.data) {
							if (a.value === emptyRules.value) {
								const d = a;
								d.value = t(emptyRules.placeholder);
								data.push(d);
							}
						}
					}
				}

				healthData.push({
					key,
					description: title,
					data: data,
					unit: unit,
					roundValuesTo: roundValuesTo,
					renderEmpty,
				});
			},
		);

		setDataset({
			results: healthData,
		} as MonitoringDataset);
	};

	return (
		<>
			{isLoading && <OverlayLoading />}
			<div>
				{dataset?.results && dataset.results.length > 0 && (
					<HealthDataVerticalTable
						tableName={Table.QUESTIONNAIRE_RESULTS}
						tableTitle={tableName}
						healthData={dataset.results}
						isLoading={isLoading}
						filter={{
							from: getDate(),
							to: getDate(),
							view: 'questionnairesResults',
							source: [],
							keys: [],
							type: 'raw',
						}}
					/>
				)}
			</div>
		</>
	);
};

export default QuestionnairesResults;
